// Centralized "your session has expired — log back in" UX.
//
// Any request transport that can receive a Warden auth-failure response — jQuery
// $.ajax (drawers + legacy JS), Turbo (frames/streams/drive), or a native
// fetch() — should route that response through here so the user gets ONE
// consistent, non-blocking toast offering to log back in, instead of a generic
// "something went wrong" error. See docs/session_expired_toast.md.
//
// The server tags these responses in TurboFailureApp with `X-Session-Expired: 1`
// (config/initializers/devise.rb). For our own same-origin traffic we also treat
// a bare HTTP 401 as an expired session, so detection still works if that header
// is ever stripped by an intermediary.

const SESSION_EXPIRED_HEADER = "X-Session-Expired"
const TOAST_MARKER = "data-session-expired-toast"
const LOGIN_PATH = "/users/sign_in"

// Returns true when a response indicates the user's session is gone. `getHeader`
// is an optional `(name) => value | null` lookup — pass `xhr.getResponseHeader`
// (jQuery) or `(name) => response.headers.get(name)` (fetch/Turbo).
//
// When a header lookup is available we require the `X-Session-Expired` tag rather
// than treating every 401 as an expired session: an authenticated user can get a
// legitimate 401 (e.g. an authorization failure on someone else's resource), and
// TurboFailureApp always tags real session-expiry 401s, so a bare 401 without the
// tag is NOT a logged-out signal. The status-only fallback covers transports that
// can't surface headers.
export function responseIndicatesSessionExpired(status, getHeader) {
  if (typeof getHeader === "function") {
    try {
      return Boolean(getHeader(SESSION_EXPIRED_HEADER))
    } catch (_e) {
      // Some transports throw when asked for an absent/forbidden header — fall
      // through to the status-based check below.
    }
  }
  return status === 401
}

// True when `fetch` followed a redirect that landed on OUR sign-in page — the shape a
// navigational request (a lazy Turbo Frame, or a native fetch with the default `Accept:
// */*`) takes when the session expired: Devise treats the HTML request as navigational and
// 302s to the login page, and fetch transparently follows it to a 200 (so there's no tag to
// key on). Requires a same-origin final URL so a cross-origin `/users/sign_in` can't spoof
// the signal.
export function responseRedirectedToLogin(response) {
  try {
    if (!response || !response.redirected) return false
    const url = new URL(response.url)
    return url.origin === window.location.origin && url.pathname === LOGIN_PATH
  } catch (_e) {
    return false
  }
}

// Surface the session-expired toast. Idempotent: the DOM is the source of truth,
// so a burst of simultaneous 401s (a page firing several requests at once) results
// in exactly one toast.
//
// The toast markup is NOT built here — it is cloned from a hidden <template> that
// the server renders with the standard `v2/toast` component and a Phosphor icon
// (app/views/v2/_session_expired_toast.html.erb), so styling, accessibility, and
// the return-to-this-page login link stay in one place. If the template isn't on
// the page (a layout that doesn't host it), we do nothing rather than invent a
// divergent toast.
export function notifySessionExpired() {
  if (document.querySelector(`[${TOAST_MARKER}]`)) return

  const template = document.getElementById("session-expired-toast-template")
  if (!template) return

  const fragment = template.content.cloneNode(true)
  applyLoginReturnTo(fragment)
  markTurboTemporary(fragment)
  document.body.appendChild(fragment)
}

// Keep the toast out of Turbo's page-cache snapshot. Otherwise, after the user follows
// the login link, a back-navigation or preview of the cached page could show
// "you've been signed out" to a user who has since logged back in.
function markTurboTemporary(root) {
  const toast = root.querySelector('[data-controller~="toast"]')
  if (toast) toast.setAttribute("data-turbo-temporary", "")
}

// Wrap a fetch-bearing target (default `window`) so every response is inspected for
// the session-expired signal. We only read `status`/`headers` — never the body — so
// the caller receives its Response completely untouched. This lets the ~30 controllers
// that call `fetch()` directly get the toast without each having to opt in. jQuery and
// Rails UJS use XMLHttpRequest (not fetch), so they're handled separately and don't
// double up here; the DOM-level de-dup makes any overlap harmless anyway.
export function installFetchSessionExpiryInterceptor(target = window) {
  if (!target || typeof target.fetch !== "function") return

  const originalFetch = target.fetch.bind(target)
  target.fetch = (...args) =>
    originalFetch(...args).then((response) => {
      try {
        // A tagged 401 (fetch that sent XHR/JSON semantics) OR a followed 302 to the
        // login page (a default `Accept: */*` fetch, which Warden treats as navigational)
        // both mean the session expired.
        if (
          responseIndicatesSessionExpired(response.status, (name) => response.headers.get(name)) ||
          responseRedirectedToLogin(response)
        ) {
          notifySessionExpired()
        }
      } catch (_e) {
        // Detection must never break the caller's fetch.
      }
      return response
    })
}

// The server renders the "Log back in" link with a bare /users/sign_in href (so an
// attacker-controlled inbound request URL is never reflected into the page). We add
// ?return_to=<current page> here, at display time, from the browser's own location.
// Works on any container that has querySelector (a DocumentFragment or an element).
export function applyLoginReturnTo(root) {
  const link = root.querySelector("[data-session-expired-login]")
  if (link) link.setAttribute("href", loginUrlReturningHere())
}

// /users/sign_in?return_to=<current page PATH>, so post-login lands back on the page
// (honored + re-validated by Users::SessionsController#store_return_to_location).
//
// Deliberately the PATHNAME ONLY — no query string, no fragment. Goodbill URLs can carry
// PHI-ish state in the query (search terms, member/claim identifiers), and putting that on
// the public sign-in URL would leak it into browser history, proxy/access logs, and auth
// telemetry; fragments are client-only and shouldn't be sent to the server at all. Returning
// to the bare path is the safe trade-off (we lose the exact query/anchor, not the page).
function loginUrlReturningHere() {
  return `${LOGIN_PATH}?return_to=${encodeURIComponent(window.location.pathname)}`
}
